/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  BarChart3,
  AlertTriangle,
  Database,
  Users,
  Inbox,
  Shield,
  Award,
} from "lucide-react";
import { Progress } from "@/components/ui/progress";
import { useGetUsage } from "@/hooks/use-subscription";
import { PlanMetricsData } from "./feature-highlights";

interface SeedListFeatureHighlightsProps {
  currentPlan: any;
  seedListMetrics?: {
    seedAccounts?: {
      used?: number;
      total?: number;
      percentage?: number;
    };
    senderProfiles?: {
      used?: number;
      total?: number;
      percentage?: number;
    };
  };
  planMetrics?: PlanMetricsData;
}

export function SeedListFeatureHighlights({
  currentPlan,
  seedListMetrics,
  planMetrics,
}: SeedListFeatureHighlightsProps) {
  // Seed Accounts metrics
  const seedAccountsUsed =
    seedListMetrics?.seedAccounts?.used ?? currentPlan?.usage?.seedAccounts ?? 0;
  const seedAccountsTotal =
    seedListMetrics?.seedAccounts?.total ?? currentPlan?.features?.seedAccounts ?? 500;
  const seedAccountsPercentage =
    seedListMetrics?.seedAccounts?.percentage ??
    Math.round((seedAccountsUsed / seedAccountsTotal) * 100);

  // Sender Profiles metrics
  const senderProfilesUsed =
    seedListMetrics?.senderProfiles?.used ?? currentPlan?.usage?.senderProfiles ?? 0;
  const senderProfilesTotal =
    seedListMetrics?.senderProfiles?.total ?? currentPlan?.features?.senderProfiles ?? 1;
  const senderProfilesPercentage =
    seedListMetrics?.senderProfiles?.percentage ??
    Math.round((senderProfilesUsed / senderProfilesTotal) * 100);

  // Email Warmup Per Day metrics
  const dailyWarmupUsed =
    planMetrics?.usageMetrics?.warmupEmails?.daily?.used ??
    currentPlan?.usage?.warmupEmails ??
    0;
  const dailyWarmupTotal =
    planMetrics?.usageMetrics?.warmupEmails?.daily?.total ??
    currentPlan?.features?.warmupEmails ??
    150;
  const dailyWarmupPercentage =
    planMetrics?.usageMetrics?.warmupEmails?.daily?.percentage ??
    (dailyWarmupTotal > 0
      ? Math.round((dailyWarmupUsed / dailyWarmupTotal) * 100)
      : 0);

  // Email Warmups Per Month metrics
  const monthlyWarmupUsed =
    planMetrics?.usageMetrics?.warmupEmails?.monthly?.used ??
    currentPlan?.usage?.warmupEmailsMonthly ??
    0;
  const monthlyWarmupTotal =
    planMetrics?.usageMetrics?.warmupEmails?.monthly?.total ??
    currentPlan?.features?.warmupEmailsMonthly ??
    4500;
  const monthlyWarmupPercentage =
    planMetrics?.usageMetrics?.warmupEmails?.monthly?.percentage ??
    (monthlyWarmupTotal > 0
      ? Math.round((monthlyWarmupUsed / monthlyWarmupTotal) * 100)
      : 0);

  const { data: usageData } = useGetUsage();

  return (
    <div className="space-y-6">
      {/* Performance Metrics */}
      <div className="bg-gradient-to-r from-primary/10 to-transparent p-4 rounded-lg border border-primary/20 mb-6">
        <h3 className="font-medium flex items-center">
          <BarChart3 className="h-5 w-5 text-primary mr-2" />
          Your Performance with {currentPlan?.name || "Current Plan"} plan
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-3">
          <div className="bg-white bg-opacity-60 dark:bg-secondary p-3 rounded-md border flex items-center gap-3">
            <div className="bg-green-100 dark:bg-green-900/20 p-2 rounded-full shrink-0">
              <Database className="h-5 w-5 text-green-600" />
            </div>
            <div className="flex-1">
              <div className="text-sm text-muted-foreground mb-1">Seed Accounts</div>
              <div className="inline-block bg-green-600 text-white px-3 py-1 rounded text-xl font-bold">
                {seedAccountsUsed.toLocaleString()} / {seedAccountsTotal.toLocaleString()}
              </div>
            </div>
          </div>
          <div className="bg-white bg-opacity-60 dark:bg-secondary p-3 rounded-md border flex items-center gap-3">
            <div className="bg-orange-100 dark:bg-orange-900/20 p-2 rounded-full shrink-0">
              <Users className="h-5 w-5 text-orange-600" />
            </div>
            <div className="flex-1">
              <div className="text-sm text-muted-foreground mb-1">Sender Profiles</div>
              <div className="inline-block bg-orange-600 text-white px-3 py-1 rounded text-xl font-bold">
                {senderProfilesUsed.toLocaleString()} / {senderProfilesTotal.toLocaleString()}
              </div>
            </div>
          </div>
        </div>
      </div>

     

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Email Warmup Per Day Card */}
        <div className="border rounded-lg p-4 bg-gradient-to-br from-blue-50 to-white dark:from-blue-900/20 dark:to-blue-900/20">
          <div className="flex items-start">
            <div className="bg-blue-100 dark:bg-blue-900/20 p-2 rounded-full mr-3">
              <BarChart3 className="h-5 w-5 text-blue-600" />
            </div>
            <div className="w-full">
              <h3 className="font-medium">Email Warmup Per Day</h3>
              <p className="text-sm text-muted-foreground mt-1">
                You're using {dailyWarmupUsed?.toLocaleString()} of your{" "}
                {dailyWarmupTotal?.toLocaleString()} daily warmup emails.
              </p>
              <div className="mt-3">
                
                <Progress
                  value={Math.min(dailyWarmupPercentage, 100)}
                  className="h-2 bg-blue-10 dark:bg-blue-900/20"
                  indicatorClassName="bg-blue-600"
                />
                {dailyWarmupUsed > dailyWarmupTotal * 0.8 && (
                  <div className="mt-2 text-xs bg-amber-50 text-amber-700 p-1.5 rounded border border-amber-100 flex items-center">
                    <AlertTriangle className="h-3 w-3 mr-1.5" />
                    <span>
                      {dailyWarmupPercentage > 100
                        ? "Exceeding your current plan limit"
                        : "Approaching your plan limit"}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Email Warmups Per Month Card */}
        <div className="border rounded-lg p-4 bg-gradient-to-br from-purple-50 to-white dark:from-purple-900/20 dark:to-purple-900/20">
          <div className="flex items-start">
            <div className="bg-purple-100 dark:bg-purple-900/20 p-2 rounded-full mr-3">
              <BarChart3 className="h-5 w-5 text-purple-600" />
            </div>
            <div className="w-full">
              <h3 className="font-medium">Email Warmups Per Month</h3>
              <p className="text-sm text-muted-foreground mt-1">
                You've used {monthlyWarmupUsed?.toLocaleString()} of your{" "}
                {monthlyWarmupTotal?.toLocaleString()} monthly warmup emails.
              </p>
              <div className="mt-3">
                
                <Progress
                  value={Math.min(monthlyWarmupPercentage, 100)}
                  className="h-2 bg-purple-10 dark:bg-purple-900/20"
                  indicatorClassName="bg-purple-600"
                />
                {monthlyWarmupUsed > monthlyWarmupTotal * 0.8 && (
                  <div className="mt-2 text-xs bg-amber-50 text-amber-700 p-1.5 rounded border border-amber-100 flex items-center">
                    <AlertTriangle className="h-3 w-3 mr-1.5" />
                    <span>
                      {monthlyWarmupPercentage > 100
                        ? "Exceeding your current plan limit"
                        : "Approaching your plan limit"}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

      </div>

      {/* Plan Value Summary */}
      <div className="border rounded-lg p-4 bg-gradient-to-r from-primary/5 to-transparent">
        <h3 className="font-medium mb-3">Your Seed List Plan Value</h3>
        <div className="space-y-2">
          <div className="flex items-start gap-2">
            <div className="h-1.5 w-1.5 rounded-full bg-primary mt-2 shrink-0" />
            <p className="text-sm text-muted-foreground">
              <strong>Warmup Impact:</strong> Your seed list configuration helps
              improve email deliverability by providing realistic engagement
              signals to email providers.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

